<template>
  <v-container fluid style="height: 100vh" v-resize="onResize">
    <v-layout column fill-height>
      <v-row class="flex-grow-0">
        <v-toolbar dense elevation="0">
          <v-icon class="pr-1" dense>mdi-history</v-icon>
          <v-btn rounded text style="font-size: 110%; padding: 0 5px" @click="selectVehicle">{{ vehicle.numericPlate }}</v-btn>
          <v-spacer></v-spacer>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionShowAll"
                     :color="all ? 'primary' : 'default'"
                     v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>grading</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.history.show_all") }}</span>
          </v-tooltip>

          <v-divider vertical class="my-2" inset />

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionVehicle" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>mdi-crosshairs-gps</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.vehicle") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionLogbook" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>mdi-text-box-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.logbook") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionRefueling" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>local_gas_station</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.refueling") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionEvents" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>event_available</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.events") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionService" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>build</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.service") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500" v-if="canRead('contract')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionContracts" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>widgets</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.contracts") }}</span>
          </v-tooltip>

          <v-divider vertical class="my-2" inset />

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="closeHistory" v-bind="attrs" v-on="on">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.close") }}</span>
          </v-tooltip>

        </v-toolbar>
      </v-row>

      <v-row class="flex-grow-0">
        <v-container class="px-4" v-if="ride !== null">
          <TextRow :title="$t('map.panel.vehicle.ride.type')" :text="ride.rideType" enum-code="ride_type" />
          <TextRow :title="$t('map.panel.vehicle.ride.state')" :text="ride.rideState" enum-code="ride_state"
                   :text-class="(ride.rideState === 'unfinished') ? 'blue--text' : ''" />
          <TextRow :title="$t('map.panel.vehicle.ride.from')" :text="formatPlace(ride.locationFrom)"
                   :flag="getCountryCode(ride.locationFrom)" />
          <TextRow title="" :text="getPlaceName(ride.locationFrom)"
                   v-if="getPlaceName(ride.locationFrom) != null" />
          <TextRow title="" :text="formatTime(ride.locationFrom)" />
          <TextRow :title="$t('map.panel.vehicle.ride.to')" :text="formatPlace(ride.locationTo)"
                   :flag="getCountryCode(ride.locationTo)" />
          <TextRow title="" :text="getPlaceName(ride.locationTo)"
                   v-if="getPlaceName(ride.locationTo) != null" />
          <TextRow title="">
            {{ formatTime(ride.locationTo) }}
            <span class="grey--text">&nbsp;•&nbsp;</span>
            <span class="blue--text">{{ formatRideDuration(ride.fromTimestamp, ride.toTimestamp)
              }}</span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.distance')">
            {{ formatDistance(ride.distance) }}
            <span class="grey--text">&nbsp;•&nbsp;</span>
            <span class="blue--text">{{ formatOdometer(ride.kmStop) }}</span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.driver_quality')" v-if="ride.driverQuality !== null">
            <v-icon small>workspace_premium</v-icon>
            <span :class="[classQuality(ride.driverQuality), 'font-weight-bold']"
                  style="padding-left: 4px">{{ formatQuality(ride.driverQuality) }}</span>
          </TextRow>
          <TextRow v-if="ride.consumptionDiff !== null && ride.consumptionDiff > 0"
                   :title="$t('map.panel.vehicle.ride.consumption')">
            {{ formatConsumption(ride.consumptionDiff) }}
            <span v-if="showAverageConsumption(ride.distance, ride.engineHours)">
                <span class="grey--text">&nbsp;•&nbsp;</span>
                <span
                  class="blue--text">{{ formatAverageComsumption(ride.consumptionDiff, ride.distance, ride.engineHours)
                  }}</span>
              </span>
          </TextRow>
          <TextRow v-else-if="vehicle.ride.tankDiff !== null && vehicle.ride.tankDiff > 0"
                   :title="$t('map.panel.vehicle.ride.consumption')">
            {{ formatConsumption(vehicle.ride.tankDiff) }}
            <span v-if="showAverageConsumption(ride.distance, ride.engineHours)">
                <span class="grey--text">&nbsp;•&nbsp;</span>
                <span class="blue--text">{{ formatAverageComsumption(ride.tankDiff, ride.distance, ride.engineHours)
                  }}</span>
              </span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.max_speed')" :text="formatMaxSpeed(ride.maxSpeed)" />
          <TextRow v-if="vehicle.maxSpeed > 0"
                   :title="$t('map.panel.vehicle.ride.over_speed')"
                   :text="formatSecondsDuration(ride.overSpeed)" />
          <TextRow v-if="ride.motorIdle > 0"
                   :title="$t('map.panel.vehicle.ride.motor_idle')"
                   :text="formatSecondsDuration(ride.motorIdle)" />
          <TextRow v-if="ride.axleLoad > 0"
                   :title="$t('map.panel.vehicle.ride.axle_load')" :text="formatWeight(ride.axleLoad)" />
          <TextRow :title="$t('map.panel.vehicle.ride.driver')"
                   :text="ride.driver.firstName + ' ' + ride.driver.lastName"
                   v-if="ride.driver !== null" />
          <TextRow :title="$t('map.panel.vehicle.ride.note')"
                   :text="ride.note"
                   v-if="ride.note !== null" />
          <ProgressRow :title="$t('map.panel.vehicle.ride.tanks')"
                       :text="formatTankLevel(ride)" color="green" :value="ride.tankSum"
                       :max-value="getTanksCapacity(vehicle)"
                       v-if="ride.tankSum && vehicle.tanks !== null && vehicle.tanks.length > 0"
                       @click="showTanks=!showTanks" />
          <div
            v-if="showTanks && vehicle.tanks !== null && vehicle.tanks.length > 0 && ride !== null && ride.tank !== null"
            class="pt-6">
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank1')" :text="formatTankLevelIndex(vehicle, 0)"
                         color="green"
                         :value="ride.tank[0]"
                         :max-value="vehicle.tanks[0].capacity" v-if="validTank(vehicle, 0)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank2')" :text="formatTankLevelIndex(vehicle, 1)"
                         color="green"
                         :value="ride.tank[1]"
                         :max-value="vehicle.tanks[1].capacity" v-if="validTank(vehicle, 1)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank3')" :text="formatTankLevelIndex(vehicle, 2)"
                         color="green"
                         :value="ride.tank[2]"
                         :max-value="vehicle.tanks[2].capacity" v-if="validTank(vehicle, 2)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank4')" :text="formatTankLevelIndex(vehicle, 3)"
                         color="green"
                         :value="ride.tank[3]"
                         :max-value="vehicle.tanks[3].capacity" v-if="validTank(vehicle, 3)" />
          </div>
        </v-container>
        <v-container class="px-4" v-else-if="summary !== null">
          <TextRow :title="$t('map.panel.vehicle.ride.from')" :text="formatPlace(summary.from)"
                   :flag="getCountryCode(summary.from)" />
          <TextRow title="" :text="getPlaceName(summary.from)"
                   v-if="getPlaceName(summary.from) != null" />
          <TextRow :title="$t('map.panel.vehicle.ride.to')" :text="formatPlace(summary.to)"
                   :flag="getCountryCode(summary.to)" />
          <TextRow title="" :text="getPlaceName(summary.to)"
                   v-if="getPlaceName(summary.to) != null" />
          <TextRow :title="$t('map.panel.vehicle.ride.distance')">
            {{ formatDistance(summary.distance) }}
            <span class="grey--text">&nbsp;•&nbsp;</span>
            <span class="blue--text">{{ formatOdometer(summary.kmStop) }}</span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.driver_quality')" v-if="summary.driverQuality !== 0">
            <v-icon small>workspace_premium</v-icon>
            <span :class="[classQuality(summary.driverQuality), 'font-weight-bold']"
                  style="padding-left: 4px">{{ formatQuality(summary.driverQuality) }}</span>
          </TextRow>

          <TextRow v-if="summary.consumptionDiff !== null && summary.consumptionDiff > 0"
                   :title="$t('map.panel.vehicle.ride.consumption')">
            {{ formatConsumption(summary.consumptionDiff) }}
            <span v-if="showAverageConsumption(summary.distance, summary.engineHours)">
                <span class="grey--text">&nbsp;•&nbsp;</span>
                <span
                  class="blue--text">{{ formatAverageComsumption(summary.consumptionDiff, summary.distance, summary.engineHours)
                  }}</span>
              </span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.elevation')">
            <v-icon small color="blue">mdi-arrow-top-right</v-icon>
            {{ formatElevation(summary.climb) }}
            &nbsp;
            <v-icon small color="red">mdi-arrow-bottom-right</v-icon>
            {{ formatElevation(summary.descent) }}
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.ride.max_speed')" :text="formatMaxSpeed(summary.maxSpeed)" />
        </v-container>
        <v-container class="px-4" v-else>
          <TextRow :title="$t('map.panel.vehicle.ride.from')" text="-" />
          <TextRow :title="$t('map.panel.vehicle.ride.to')" text="-" />
          <TextRow :title="$t('map.panel.vehicle.ride.distance')" text="-" />
          <TextRow :title="$t('map.panel.vehicle.ride.elevation')" text="-" />
          <TextRow :title="$t('map.panel.vehicle.ride.max_speed')" text="-" />
        </v-container>
      </v-row>

      <v-row class="overflow-y-auto" style="max-height: 100%" ref="resizableTable" align-content="start">
        <v-data-table style="max-height: 100vh; width: 500px; max-width: 500px; margin-top: 8px"
                      :items="rides"
                      item-key="fromTimestamp"
                      class="elevation-0"
                      hide-default-header
                      hide-default-footer
                      fixed-header
                      :items-per-page="-1"
                      :height="tableHeight"
                      ref="table"
        >
          <template v-slot:header="">
            <thead>
            <tr>
              <th>
                {{ $t("map.panel.history.column.from_ts") }} / {{ $t("map.panel.history.column.to_ts") }}
                <br />
                {{ $t("map.panel.history.column.duration") }}
              </th>
              <th>
                {{ $t("map.panel.history.column.from") }} / {{ $t("map.panel.history.column.to") }}
                <br />
                {{ $t("map.panel.history.column.type_driver") }}
              </th>
              <th style="text-align: end">
                {{ $t("map.panel.history.column.odometer") }} / {{ $t("map.panel.history.column.distance") }}
                <br />
                {{ $t("map.panel.history.column.consumption") }}
              </th>
            </tr>
            </thead>
          </template>
          <template v-slot:item="props">
            <tr v-on:click="onClickRide(props.item)"
                v-on:contextmenu="rowMenuClick($event, props.item)"
                v-bind:class="{'yellow lighten-4': isActiveRide(props.item)}"
                :ref="'history' + props.item.fromTimestamp"
                :id="'history' + props.item.fromTimestamp">
              <td style="white-space: nowrap">
                {{ formatShortTime(props.item.fromTimestamp) }}
                <br />
                {{ formatShortTime(props.item.toTimestamp) }}
                <br />
                <span class="font-italic">{{ formatRideDuration(props.item.fromTimestamp, props.item.toTimestamp)
                  }}</span>
              </td>
              <td style="white-space: nowrap; text-overflow: ellipsis; overflow: hidden; max-width: 310px">
                <country-flag class="small-flag-move" v-if="getLocationPlaceFlag(props.item.locationFrom)"
                              :country="getLocationPlaceFlag(props.item.locationFrom)" size="small" />
                {{ formatPlace(props.item.locationFrom) }}
                <br />
                <country-flag class="small-flag-move" v-if="getLocationPlaceFlag(props.item.locationTo)"
                              :country="getLocationPlaceFlag(props.item.locationTo)" size="small" />
                {{ formatPlace(props.item.locationTo) }}
                <br />
                <span class="font-italic">{{ $t("enum.ride_type." + props.item.rideType) }}</span>
                &nbsp;•&nbsp;
                <span class="ride-order">{{ props.item.order }}</span>
                <span v-if="props.item.driver !== null">
                  &nbsp;•&nbsp;{{ props.item.driver.firstName + " " + props.item.driver.lastName }}
                </span>
              </td>
              <td style="white-space: nowrap; text-align: end">
                {{ formatOdometer(props.item.kmStop) }}
                <br />
                {{ formatDistance(props.item.distance) }}
                <br />
                <span v-if="props.item.refueling !== null" class="font-weight-bold">{{ props.item.refueling + " l" }}</span>
                <span v-else-if="props.item.rideType === 'fuelloss'" class="font-weight-bold">{{ props.item.tankDiff + " l" }}</span>
                <span v-else>{{ formatAverageComsumption(props.item) || "-" }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
        <v-menu
          v-model="contextMenu"
          :position-x="contextMenuX"
          :position-y="contextMenuY"
          absolute
          offset-y
        >
          <v-list>
            <v-list-item v-for="(item, index) in getRideContextMenuItems(contextRide)"
                         :key="index"
                         link
                         @click="rowMenuItemClick(item)"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>
    </v-layout>
  </v-container>
</template>

<script>
import TextRow from "@/components/properties/TextRow";
import ProgressRow from "@/components/properties/ProgressRow";
import utils from "@/utils";
import { DateTime } from "luxon";

export default {
  name: "SideHistory",

  components: { ProgressRow, TextRow },

  props: {
    vehicleSidebarClick: {}
  },

  data() {
    return {
      vehicleFilter: null,
      menu: false,
      date: null,
      panels: [],
      tableHeight: 0,
      showTanks: false,

      contextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      contextRide: null
    };
  },

  methods: {
    onResize() {
      this.tableHeight = window.innerHeight - this.$refs.resizableTable.getBoundingClientRect().y - 10;
    },

    closeHistory() {
      this.$store.dispatch("history/clear");
      this.$router.routeToMap();
    },

    selectVehicle() {
      this.$store.dispatch("dialogs/showDialog", {
        dialog: "vehicleSelection", options: true
      });
    },

    actionVehicle() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/vehicle",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionShowAll() {
      this.$store.dispatch("history/fetch", {
        all: !this.$store.state.history.all,
        periodFrom: this.$store.state.history.periodFrom,
        periodTo: this.$store.state.history.periodTo,
        rideFrom: (this.$store.state.history.ride !== null) ? this.$store.state.history.ride.fromTimestamp : null
      });
    },

    actionLogbook() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/logbook",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionRefueling() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/refueling",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionEvents() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/events",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionContracts() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/contracts",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionService() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/service",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    onClickRide(ride) {
      this.$store.dispatch("history/fetch", {
        all: this.$store.state.history.all,
        periodFrom: this.$store.state.history.periodFrom,
        periodTo: this.$store.state.history.periodTo,
        rideFrom: (this.$store.state.history.ride === null || ride.fromTimestamp !== this.$store.state.history.ride.fromTimestamp)
          ? ride.fromTimestamp : null
      });

      this.$emit("clicked", ride);
    },

    rowMenuClick(event, item) {
      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;
      this.contextRide = item;
      this.contextMenu = true;

      event.preventDefault();
    },

    getRideContextMenuItems(ride) {
      let menu = [];

      if (ride !== null) {
        menu.push({ name: "select", title: this.$t("map.panel.history.ride_menu.select") });
        if (ride.rideState !== "unfinished" && this.accessRole("admin"))
          menu.push({ name: "update", title: this.$t("map.panel.history.ride_menu.update") });
      }

      return menu;
    },

    rowMenuItemClick(item) {
      switch (item.name) {
        case "select":
          this.onClickRide(this.contextRide);
          break;
        case "update":
          this.$store.dispatch("dialogs/showDialog", { dialog: "rideUpdate", options: this.contextRide });
          break;
      }
    },

    formatShortTime(ts) {
      return DateTime.fromISO(ts).toLocaleString(DateTime.TIME_24_SIMPLE);
    },

    getLocationPlace(location) {
      if (location !== null && location !== undefined && location.place !== null)
        return utils.formatLocation(location.place);
      else
        return "-";
    },

    getLocationPlaceFlag(location) {
      if (location !== null && location !== undefined && location.place !== null)
        return location.place.countryCode;
      else
        return "";
    },

    getDriver(ride) {
      if (ride.driver !== null)
        return ride.driver.firstName + " " + ride.driver.lastName;
      else if (ride.driverCode !== null)
        return ride.driverCode;
      else
        return "-";
    },

    getValueByRideType(vehicle) {
      if (vehicle.rideType === "refueling" || vehicle.rideType === "fuelloss")
        return utils.formatFuel(vehicle.tankSum, vehicle.tankDiff);
      else
        return utils.formatDistance(vehicle.distance);
    },

    getRideTime(ride) {
      return DateTime.fromISO(ride.fromTimestamp).toLocaleString(DateTime.TIME_24_SIMPLE)
        + " až "
        + DateTime.fromISO(ride.toTimestamp).toLocaleString(DateTime.TIME_24_SIMPLE)
        + " ("
        + DateTime.fromISO(ride.toTimestamp).toRelative(ride.fromTimestamp, true)
        + ")";
    },

    isActiveRide(ride) {
      return this.$store.state.history.ride !== null && ride.fromTimestamp === this.$store.state.history.ride.fromTimestamp;
    },

    formatPlace(location) {
      if (location !== null)
        return utils.formatLocation(location.place);
      else
        return null;
    },

    getCountryCode(location) {
      if (location !== null)
        return location.countryCode;
      else
        return null;
    },

    getPlaceName(location) {
      if (location !== null && location.place !== null)
        return location.place.name;
      else
        return null;
    },

    formatTime(location) {
      if (location !== null)
        return DateTime.fromISO(location.timestamp).toLocaleString(DateTime.DATETIME_SHORT);
      else
        return null;
    },

    formatRideDuration(from, to) {
      if (from !== null && to !== null)
        return DateTime.fromISO(to).diff(DateTime.fromISO(from)).toFormat("h'h' m'm'");
      else
        return null;
    },

    formatSpeed(location) {
      if (location !== null && location.speed > 0)
        return location.speed + " km/h";
      else
        return null;
    },

    formatMaxSpeed(speed) {
      if (speed > 0)
        return speed + " km/h";
      else
        return null;
    },

    formatQuality(value) {
      return utils.numberToString(value, 1, true);
    },

    classQuality(value) {
      return utils.colorClassQuality(value);
    },

    validTank(vehicle, index) {
      return (vehicle.tanks !== null && index < vehicle.tanks.length &&
        vehicle.ride !== null && vehicle.ride.tank !== null && index < vehicle.ride.tank.length);
    },

    getTanksCapacity(vehicle) {
      if (vehicle.tanks !== null)
        return vehicle.tanks
          .filter(x => x.tankType === "main")
          .reduce(((sum, x) => sum + x.capacity), 0);
      else
        return 0;
    },

    formatTankLevelIndex(vehicle, index) {
      return utils.formatFuel(vehicle.ride.tank[index]);
    },

    formatTankLevel(ride) {
      return utils.formatFuel(ride.tankSum);
    },

    formatDistance(distance) {
      return utils.formatDistance(distance);
    },

    formatOdometer(value) {
      return utils.formatOdometer(value);
    },

    formatElevation(elevation) {
      if (elevation >= 500)
        return utils.formatDistance(elevation);
      else
        return elevation + " m";
    },

    formatConsumption(consumption) {
      if (consumption !== null && consumption > 0)
        return utils.numberToString(consumption) + " l";
      else
        return null;
    },

    formatAverageComsumption(diff, distance, engineHours) {
      if (this.vehicle.typeCode === "machine") {
        let acon = utils.averageEngineHoursConsumption(diff, engineHours, this.vehicle);

        if (acon !== null) {
          return utils.numberToString(acon) + " l/h";
        } else
          return "";
      } else {
        let acon = utils.averageDistanceConsumption(diff, distance, this.vehicle);

        if (acon !== null) {
          return utils.numberToString(acon) + " l/100km";
        } else
          return "";
      }
    },

    formatDuration(value) {
      if (value >= 0)
        return utils.formatHours(value);
      else
        return utils.formatHours(0);
    },

    formatSecondsDuration(value) {
      if (value >= 0)
        return utils.formatHours(value, true, false);
      else
        return utils.formatHours(0, true, false);
    },

    formatWeight(value) {
      return utils.formatWeight(value);
    },

    showAverageConsumption(distance, engineHours) {
      if (this.vehicle.typeCode === "machine")
        return engineHours >= 300;
      else
        return distance >= 500;
    },

    refreshRide() {
      if (this.$store.state.history.ride === null) {
        this.$nextTick().then(() => {
          this.onResize();
        });

        this.updateUrl();
        return;
      }

      this.$nextTick().then(() => {
        const ref = this.$refs["history" + this.$store.state.history.ride.fromTimestamp];
        if (ref !== null && ref !== undefined) {
          const wrapper = this.$refs["table"].$el.querySelector("div.v-data-table__wrapper");

          if (ref.offsetTop < wrapper.scrollTop + ref.clientHeight ||
            ref.offsetTop + ref.offsetHeight >= wrapper.scrollTop + wrapper.clientHeight) {

            const goToOptions = {
              container: wrapper,
              duration: 300,
              easing: "easeInOutCubic",
              offset: 200
            };

            const element = document.getElementById("history" + this.$store.state.history.ride.fromTimestamp);

            if (element !== null)
              this.$vuetify.goTo(element, goToOptions);

            //this.$vuetify.goTo("history" + this.$store.state.history.ride.fromTimestamp);
            //ref.scrollIntoView({ block: 'center', behavior: "smooth" });
          }
        }

        this.updateUrl();
        this.onResize();
      });
    },

    updateUrl() {
      this.$router.replace({
        name: "map/history",
        params: {
          id: this.$store.state.history.vehicleId
        },
        query: {
          ...this.$route.query,
          day: DateTime.fromISO(this.$store.state.history.periodFrom).toFormat("yyyy-MM-dd"),
          ride: (this.$store.state.history.ride !== null) ? this.$store.state.history.ride.fromTimestamp : undefined
        }
      }).catch(() => {
      });
    }
  },

  watch: {
    "$store.state.history.vehicleId": function() {
      this.updateUrl();
    },

    "$store.state.history.periodFrom": function() {
      this.updateUrl();
    },

    "$store.state.history.ride": function() {
      this.refreshRide();
    }
  },

  computed: {
    vehicle() {
      return this.$store.state.history.vehicle;
    },

    summary() {
      return this.$store.state.history.summary;
    },

    ride() {
      return this.$store.state.history.ride;
    },

    rides() {
      return this.$store.state.history.rides;
    },

    all() {
      return this.$store.state.history.all;
    }
  },

  mounted() {
    this.refreshRide();
  }
};
</script>

<style lang="sass" scoped>
.list-item-content
  padding: 4px 0 !important

.icon
  padding: 0 5px 0 0 !important
  color: steelblue !important

.list-item-subtitle
  padding: 1px 0 !important
  font-size-adjust: 0.48

.selected
  background: #d3e6e9

.list-item-content-edit
  padding: 4px 0 10px 0 !important

.small-flag-move
  margin: -15px -20px !important

.ride-order
  background-color: rgba(100, 100, 100, 1.0)
  color: rgba(240, 240, 240, 1.0)
  border-radius: 5px
  padding: 0 3px
  font-weight: bold
  font-size-adjust: 0.5

</style>